import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { documentsAPI, hierarchyAPI, workflowsAPI } from '../api/client';
import DocumentList from '../components/DocumentList';
import SubmitForApprovalModal from '../components/SubmitForApprovalModal';
import StatusMultiSelect, { DEFAULT_STATUSES, isDefaultSelection } from '../components/StatusMultiSelect';
import ScopeToggle, { scopeFromParams } from '../components/ScopeToggle';
import PolicyAreaMultiSelect from '../components/PolicyAreaMultiSelect';
import RegulatoryReferenceMultiSelect from '../components/RegulatoryReferenceMultiSelect';
import DocumentTypeMultiSelect from '../components/DocumentTypeMultiSelect';
import { extractErrorMessage } from '../utils/errors';
import { useResourcePermissions } from '../auth/useResourcePermissions';
import { useAuth } from '../auth/AuthContext';

/**
 * Which statuses a given URL asks for.
 *
 * `status` absent means the DEFAULT (everything except archived), not "no
 * filter" -- that is the whole of GitHub #36. Any `exclude_status` the
 * dashboard put in the URL is folded in here rather than being sent as a
 * second, independent filter, so the control on screen always shows exactly
 * what is being asked for and the two can't contradict each other.
 */
function statusesFromParams(searchParams) {
  const explicit = searchParams.getAll('status');
  const excluded = searchParams.getAll('exclude_status');
  const base = explicit.length ? explicit : DEFAULT_STATUSES;
  const kept = base.filter((s) => !excluded.includes(s));
  return kept.length ? kept : DEFAULT_STATUSES;
}

/**
 * Repeatable numeric ids out of the URL.
 *
 * Number(), because LookupMultiSelect matches on `selectedIds.includes(item.id)`
 * and item.id is whatever the API returned -- a number. Handing it the strings
 * getAll() produces makes every checkbox read as unticked while the filter is
 * genuinely applied.
 */
function idsFromParams(searchParams, key) {
  return searchParams.getAll(key).map(Number).filter(Number.isFinite);
}

function DocumentsPage() {
  // The URL is the source of truth for filters, so dashboard tiles can deep-link
  // straight into a filtered view (e.g. /documents?status=active&expires_from=...).
  const [searchParams, setSearchParams] = useSearchParams();
  const { canCreate: canCreateDocument, canUpdate: canUpdateDocument, canDelete: canDeleteDocument } =
    useResourcePermissions('documents');
  const { homeScope, can } = useAuth();
  // Affordance hint, not the control (issue #150) -- document_visibility_
  // service.status_restriction is what actually clamps a caller with neither
  // grant to status == active (plus their own owned/contributed/open-gate
  // documents, which this hint does not know about; those still appear in
  // the default unfiltered list, they just lose the picker). document_drafts
  // is hierarchical, so can() here means "somewhere", the right question for
  // a UI hint: being wrong costs a hidden-but-harmless control, never a false
  // sense of access.
  const canSeeStatusFilter = can('document_drafts', 'read') || can('documents', 'update');
  const hasHomeScope = Boolean(homeScope?.nodes?.length);
  const [documents, setDocuments] = useState([]);
  const [nodes, setNodes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState(searchParams.get('search') || '');
  const [selectedStatuses, setSelectedStatuses] = useState(() => statusesFromParams(searchParams));
  const [scope, setScope] = useState(() => scopeFromParams(searchParams, hasHomeScope));
  // On-page controls since issue #138 (which also closes half of #124).
  // Before that these arrived ONLY from the dashboard's hero search, which
  // that same issue removed -- so without a control here they would have
  // become unreachable rather than merely inconvenient.
  //
  // NUMBERS, not the strings URLSearchParams.getAll hands back.
  // LookupMultiSelect tests `selectedIds.includes(item.id)` against the id the
  // API returned, which is a number -- so leaving these as strings makes the
  // filter apply correctly while the control renders "All policy areas", and
  // the next press of Search silently clears it. Caught in the browser on a
  // deep link, not by the compiler.
  const [selectedPolicyAreaIds, setSelectedPolicyAreaIds] = useState(
    () => idsFromParams(searchParams, 'policy_area_id'));
  const [selectedRegulatoryReferenceIds, setSelectedRegulatoryReferenceIds] = useState(
    () => idsFromParams(searchParams, 'regulatory_reference_id'));
  // Issue #202. Same NUMBERS-not-strings rule as the two above.
  const [selectedDocumentTypeIds, setSelectedDocumentTypeIds] = useState(
    () => idsFromParams(searchParams, 'document_type_id'));
  const [submitTargetDoc, setSubmitTargetDoc] = useState(null);
  // Separate from submitTargetDoc: this drives submission of a freshly-opened
  // periodic-review revision, which posts to a different endpoint
  // (workflowsAPI.submitRevisionForApproval) than an initial submission.
  const [reviewTarget, setReviewTarget] = useState(null);

  // Read straight from the URL, which stays the source of truth for every
  // filter. Policy area and regulatory reference now ALSO have on-page controls
  // (the two state vars above mirror these); expiry range, hierarchy node and
  // applicability group still have none and arrive only from a dashboard tile
  // or a shared link.
  const expiresFrom = searchParams.get('expires_from') || '';
  const expiresTo = searchParams.get('expires_to') || '';
  const policyAreaIds = searchParams.getAll('policy_area_id');
  const applicabilityGroupIds = searchParams.getAll('applicability_group_id');
  const hierarchyNodeIds = searchParams.getAll('hierarchy_node_id');
  const regulatoryReferenceIds = searchParams.getAll('regulatory_reference_id');
  const documentTypeIds = searchParams.getAll('document_type_id');

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const [docsRes, nodesRes] = await Promise.all([
        documentsAPI.list({
          search: searchParams.get('search') || '',
          // Always explicit, never "absent means everything": the backend has
          // no notion of the page's default, and exclude_status is folded into
          // this list rather than sent alongside it. scope follows the same
          // rule (issue #17) -- resolved here from the URL, same as status,
          // rather than trusted from the `scope` state var below.
          status: statusesFromParams(searchParams),
          scope: scopeFromParams(searchParams, hasHomeScope),
          expires_from: expiresFrom,
          expires_to: expiresTo,
          policy_area_id: policyAreaIds,
          applicability_group_id: applicabilityGroupIds,
          hierarchy_node_id: hierarchyNodeIds,
          regulatory_reference_id: regulatoryReferenceIds,
          document_type_id: documentTypeIds,
        }),
        hierarchyAPI.getNodes(),
      ]);
      setDocuments(docsRes.data);
      setNodes(nodesRes.data);
      setError(null);
    } catch (err) {
      console.error('Error fetching documents:', err);
      setError(extractErrorMessage(err));
    } finally {
      setLoading(false);
    }
    // searchParams is the main dependency -- the derived arrays above are
    // recomputed from it on every render. hasHomeScope also matters here
    // (it decides scope's default when the URL doesn't say), but it only
    // ever flips once per session as /me resolves, not per keystroke.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams, hasHomeScope]);

  // Refetch whenever the URL filters change, including on first load and on
  // back/forward navigation.
  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Keep the text/status/scope inputs in sync when the URL changes underneath them.
  useEffect(() => {
    setSearchTerm(searchParams.get('search') || '');
    setSelectedStatuses(statusesFromParams(searchParams));
    setScope(scopeFromParams(searchParams, hasHomeScope));
    setSelectedPolicyAreaIds(idsFromParams(searchParams, 'policy_area_id'));
    setSelectedRegulatoryReferenceIds(idsFromParams(searchParams, 'regulatory_reference_id'));
    setSelectedDocumentTypeIds(idsFromParams(searchParams, 'document_type_id'));
  }, [searchParams, hasHomeScope]);

  // Only the filters with NO on-page control -- policy area and regulatory
  // reference are deliberately absent since issue #138 gave them one, or this
  // strip would render with nothing in it but a Clear button.
  const hasUrlFilters = Boolean(
    expiresFrom || expiresTo || applicabilityGroupIds.length || hierarchyNodeIds.length
  );

  const clearFilters = () => setSearchParams({});

  const handleDeleteDocument = async (id) => {
    if (window.confirm('Are you sure you want to archive this document?')) {
      try {
        await documentsAPI.delete(id);
        fetchData();
      } catch (err) {
        console.error('Error deleting document:', err);
        setError('Failed to delete document');
      }
    }
  };

  // Shared by the Search button and the scope toggle -- both write into the
  // SAME searchParams update, so a toggle click can apply immediately (a
  // two-state control needs no "confirm" step) while text/status filters keep
  // requiring Search, without two different code paths writing the URL.
  // `overrides.scope`, when given, wins over the `scope` state var -- needed
  // because ScopeToggle's onChange fires before that state's setter has
  // flushed, so reading `scope` here would apply the PREVIOUS value.
  const applyFilters = (overrides = {}) => {
    const next = new URLSearchParams(searchParams);
    if (searchTerm) {
      next.set('search', searchTerm);
    } else {
      next.delete('search');
    }
    // The default is expressed by the ABSENCE of `status`, so a bare
    // /documents URL keeps meaning "everything except archived" and a shared
    // link reproduces exactly what the sharer was looking at.
    next.delete('status');
    if (!isDefaultSelection(selectedStatuses)) {
      selectedStatuses.forEach((s) => next.append('status', s));
    }
    // Folded into the status selection at read time, so leaving it in the URL
    // would apply it twice and let the two disagree.
    next.delete('exclude_status');

    // Same rule as status: absence means the resolved default (issue #17),
    // so a bare /documents URL keeps meaning "whatever this viewer's default
    // is" and a shared link reproduces exactly what the sharer saw.
    const effectiveScope = overrides.scope !== undefined ? overrides.scope : scope;
    const defaultScope = hasHomeScope ? 'mine' : 'all';
    if (effectiveScope === defaultScope) {
      next.delete('scope');
    } else {
      next.set('scope', effectiveScope);
    }
    // Plain repeatable params, the same shape the dashboard used to write and
    // the backend already accepts (routers/documents.py::list_documents).
    // Absence means "no filter" here -- unlike status and scope above, there is
    // no non-empty default to express by leaving them out.
    next.delete('policy_area_id');
    selectedPolicyAreaIds.forEach((id) => next.append('policy_area_id', id));
    next.delete('regulatory_reference_id');
    selectedRegulatoryReferenceIds.forEach((id) => next.append('regulatory_reference_id', id));
    next.delete('document_type_id');
    selectedDocumentTypeIds.forEach((id) => next.append('document_type_id', id));

    setSearchParams(next);
  };

  const handleSearch = () => applyFilters();

  const handleScopeChange = (newScope) => {
    setScope(newScope);
    applyFilters({ scope: newScope });
  };

  const handleSubmitForApproval = async (templateId) => {
    await workflowsAPI.submitForApproval(submitTargetDoc.id, templateId);
    setSubmitTargetDoc(null);
    setError(null);
    fetchData();
  };

  // Starting a review from the list (rather than the document page) so a
  // "Coming Due" dashboard tile can be acted on row-by-row without opening
  // each document first. Opens the revision, then reuses the same picker
  // modal DocumentViewPage uses to choose a workflow and submit it.
  const handleStartReview = async (doc) => {
    try {
      const res = await documentsAPI.createRevision(doc.id, 'periodic_review');
      setReviewTarget({ revisionId: res.data.id, hierarchyNodeId: doc.hierarchy_node_id, documentId: doc.id });
    } catch (err) {
      console.error('Error starting review:', err);
      setError(extractErrorMessage(err));
    }
  };

  const handleSubmitReview = async (templateId) => {
    await workflowsAPI.submitRevisionForApproval(reviewTarget.revisionId, templateId);
    setReviewTarget(null);
    setError(null);
    fetchData();
  };

  if (loading) {
    return <div className="loading">Loading documents...</div>;
  }

  return (
    <div>
      <div className="page-header">
        <h2>📄 Documents</h2>
        {canCreateDocument && (
          <Link to="/documents/new" className="btn btn-primary">
            ➕ New Document
          </Link>
        )}
      </div>

      {error && <div className="alert alert-error">{error}</div>}

      <div className="card">
        <div className="card-header">
          <h3>Filter & Search</h3>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '15px' }}>
          <div className="form-group">
            <label>Search</label>
            <input
              type="text"
              placeholder="Search by title or content..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
          {/* BEFORE Statuses, as issue #138 asks -- "most users should only
              see active", so the status control is the one people should reach
              for least and it should not be the first thing under Search. */}
          <div className="form-group">
            <label>Policy Areas</label>
            <PolicyAreaMultiSelect
              selectedIds={selectedPolicyAreaIds}
              onChange={setSelectedPolicyAreaIds}
            />
          </div>
          <div className="form-group">
            <label>Document Types</label>
            <DocumentTypeMultiSelect
              selectedIds={selectedDocumentTypeIds}
              onChange={setSelectedDocumentTypeIds}
            />
          </div>
          <div className="form-group">
            <label>Regulatory References</label>
            <RegulatoryReferenceMultiSelect
              selectedIds={selectedRegulatoryReferenceIds}
              onChange={setSelectedRegulatoryReferenceIds}
            />
          </div>
          {canSeeStatusFilter && (
            <div className="form-group">
              <label>Statuses</label>
              <StatusMultiSelect value={selectedStatuses} onChange={setSelectedStatuses} />
            </div>
          )}
          <div className="form-group">
            <label>Locations</label>
            <ScopeToggle value={scope} onChange={handleScopeChange} hasHomeScope={hasHomeScope} />
          </div>
          <div className="form-group" style={{ display: 'flex', alignItems: 'flex-end' }}>
            <button className="btn btn-primary" onClick={handleSearch}>
              🔍 Search
            </button>
          </div>
        </div>

        {hasUrlFilters && (
          <div style={{
            marginTop: '10px', paddingTop: '12px', borderTop: '1px solid var(--border)',
            display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap',
          }}>
            <span style={{ fontSize: '13px', color: 'var(--muted)' }}>
              Filtered from dashboard:
            </span>
            {(expiresFrom || expiresTo) && (
              <span className="badge" style={{ background: 'var(--info-bg)', color: 'var(--info-ink)' }}>
                Expires {expiresFrom || '…'} to {expiresTo || '…'}
              </span>
            )}
            {hierarchyNodeIds.length > 0 && (
              <span className="badge" style={{ background: 'var(--neutral-bg)', color: 'var(--neutral-ink)' }}>
                {hierarchyNodeIds.length} hierarchy location(s)
              </span>
            )}
            {/* No chips for policy area or regulatory reference: both now have
                their own controls above, which is where the answer comes from,
                and a read-only chip repeating a live control is how the two get
                to disagree (the same reason exclude_status has no chip). */}
            {applicabilityGroupIds.length > 0 && (
              <span className="badge" style={{ background: 'var(--neutral-bg)', color: 'var(--neutral-ink)' }}>
                {applicabilityGroupIds.length} applicability group(s)
              </span>
            )}
            {/* No "excluding ..." chip: exclude_status is folded into the
                Statuses control above, which is now the one place the answer
                comes from. */}
            <button className="btn btn-secondary btn-small" onClick={clearFilters}>
              Clear filters
            </button>
          </div>
        )}
      </div>

      <DocumentList
        documents={documents}
        nodes={nodes}
        onDelete={handleDeleteDocument}
        onRefresh={fetchData}
        onSubmitForApproval={setSubmitTargetDoc}
        onStartReview={handleStartReview}
        canUpdate={canUpdateDocument}
        canDelete={canDeleteDocument}
        scope={scope}
        onShowAllLocations={() => handleScopeChange('all')}
      />

      {submitTargetDoc && (
        <SubmitForApprovalModal
          hierarchyNodeId={submitTargetDoc.hierarchy_node_id}
          documentId={submitTargetDoc.id}
          isResuming={!!submitTargetDoc.pending_workflow_instance_id}
          pendingTemplateName={submitTargetDoc.pending_workflow_template_name}
          onSave={handleSubmitForApproval}
          onClose={() => setSubmitTargetDoc(null)}
        />
      )}

      {reviewTarget && (
        <SubmitForApprovalModal
          title="Start Periodic Review"
          hierarchyNodeId={reviewTarget.hierarchyNodeId}
          documentId={reviewTarget.documentId}
          onSave={handleSubmitReview}
          onClose={() => setReviewTarget(null)}
        />
      )}
    </div>
  );
}

export default DocumentsPage;
